import { Button, Hinweisdialog } from './primitives'
import { APP_VERSION, type Aenderung } from '@/features/changelog/fassungen'
import { merkeGesehen } from '@/features/changelog/gesehen'

/**
 * Was die eingespielte Fassung an neuen Funktionen mitbringt.
 *
 * Erscheint einmal nach jedem Update, das welche hat. Eine Fassung mit reinen
 * Fehlerbehebungen meldet sich nicht — sie hätte nichts zu sagen, was der Nutzer tun
 * könnte, und ein Dialog, der nur sich selbst ankündigt, hält im Einsatz auf. Ob dieser
 * Dialog an der Reihe ist, entscheidet `Hinweise.tsx`.
 *
 * Anders als der Änderungshinweis für Karten merkt sich dieser Dialog den Stand **bei
 * jedem Weg hinaus**, auch beim Wegwischen: Eine neue Funktion ist kein Vorgang, der
 * offen bleibt. Wer sie übersieht, verpasst nichts Verbindliches — bei einer geänderten
 * Einsatzunterlage wäre das anders, und genau deshalb kommt jene wieder.
 */
/**
 * Höchstens so viele Funktionen im Dialog.
 *
 * Wer mehrere Fassungen übersprungen hat, bekäme sonst eine Liste, die den Knopf unter
 * den unteren Bildschirmrand schiebt — und niemand liest im Einsatz zwölf Punkte. Was
 * darüber hinausgeht, wird gezählt und steht vollständig unter „Mehr“.
 */
const HOECHSTENS = 4

export function NeuerungenPrompt({
  funktionen,
  onErledigt,
  onAlleAenderungen,
}: {
  funktionen: Aenderung[]
  onErledigt: () => void
  /** Führt zum vollständigen Verlauf. Welcher Dialog dann folgt, entscheidet `Hinweise.tsx`. */
  onAlleAenderungen: () => void
}) {
  const schliessen = async () => {
    await merkeGesehen()
    onErledigt()
  }

  const gezeigt = funktionen.slice(0, HOECHSTENS)
  const weitere = funktionen.length - gezeigt.length

  return (
    <Hinweisdialog titel="Neu in dieser Fassung" onAbbruch={() => void schliessen()}>
      <p className="text-sm text-ink">
        <strong className="font-semibold">
          Die App ist jetzt Fassung <span className="tabular-nums">{APP_VERSION}</span>.
        </strong>{' '}
        {funktionen.length === 1 ? 'Neu ist:' : 'Neu sind:'}
      </p>

      <ul className="mt-2 space-y-1.5">
        {gezeigt.map((funktion) => (
          <li key={funktion.text} className="flex gap-2 text-sm leading-relaxed text-ink">
            <span aria-hidden className="mt-2 size-1.5 shrink-0 rounded-full bg-accent" />
            <span>{funktion.text}</span>
          </li>
        ))}
      </ul>

      {weitere > 0 && (
        <p className="mt-2 text-sm text-muted">
          {weitere === 1 ? 'und eine weitere Neuerung' : `und ${weitere} weitere Neuerungen`}
        </p>
      )}

      <div className="mt-3 flex items-center gap-3">
        <Button variant="primary" onClick={() => void schliessen()}>
          Verstanden
        </Button>
        {/* Der Weg zur vollständigen Liste. Sie tritt an die Stelle dieses Dialogs,
            statt sich darüberzulegen — zwei Blätter übereinander wären im Einsatz
            eine Ebene zu viel. Der Verlauf merkt den Stand selbst. */}
        <button
          type="button"
          onClick={onAlleAenderungen}
          className="tap -mr-2 shrink-0 px-2 text-sm text-muted hover:text-ink"
        >
          Alle Änderungen
        </button>
      </div>
    </Hinweisdialog>
  )
}
